import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import useUser from '../global/useUser';
import { LoadedUser } from '../../interfaces';
import {
  getArtistOptionsFromOrganizationBusiness,
  getOrganizationBusinessOptions,
  getServiceFromOrganizationBusiness,
  getServiceOptionsFromOrganizationBusiness,
} from '../../helpers/organizations';
import { getOrganizationBusinesses } from '../../services/organizations';
import { OrganizationBusiness } from '../../types/organizations';
import { isUserAdminOrManager } from '../../utils/global/helper';

function useBusinessOrganization() {
  const { user } = useUser<LoadedUser>();
  const organizationId = user?.business?.organizationId || null;
  const canManageOrganization = !!user && isUserAdminOrManager(user);

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['organizationBusinesses', organizationId, user?.userId],
    queryFn: async () => {
      if (!organizationId) return null;
      return getOrganizationBusinesses(organizationId);
    },
    enabled: !!organizationId && !!user?.userId && canManageOrganization,
  });

  const businesses = useMemo(() => data?.businesses ?? [], [data?.businesses]);
  const hasMultipleBusinesses = canManageOrganization && businesses.length > 1;

  const businessOptions = useMemo(
    () => getOrganizationBusinessOptions(businesses),
    [businesses],
  );

  const getBusinessById = (businessId?: string | null) =>
    businesses.find(business => business.businessId === businessId) ?? null;

  const getArtistOptionsForBusiness = (businessId?: string | null) => {
    const business = getBusinessById(businessId) as OrganizationBusiness | null;
    return getArtistOptionsFromOrganizationBusiness(business ?? undefined);
  };

  const getServiceOptionsForBusiness = (businessId?: string | null) => {
    const business = getBusinessById(businessId) as OrganizationBusiness | null;
    return getServiceOptionsFromOrganizationBusiness(business ?? undefined);
  };

  const getServiceForBusiness = (
    businessId?: string | null,
    serviceId?: string | number | null,
  ) => {
    const business = getBusinessById(businessId) as OrganizationBusiness | null;
    return getServiceFromOrganizationBusiness(business ?? undefined, serviceId);
  };

  return {
    organization: data ?? null,
    organizationId,
    businesses,
    businessOptions,
    hasMultipleBusinesses,
    getBusinessById,
    getArtistOptionsForBusiness,
    getServiceOptionsForBusiness,
    getServiceForBusiness,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
}

export default useBusinessOrganization;
